import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uploadWorkflowMediaRequest } from "../../../store/slices/workflow";
import { applyMediaUploadResultToNodes } from "../utils/workflowInspectorUx";
import { findPendingUploadsDeep } from "../utils/workflowMediaUtils";

/**
 * Flushes pending inspector media uploads before SAVE_WORKFLOW.
 */
export function useWorkflowMediaFlush({
  nodesRef,
  updateNodeData,
  workflowSave,
  inspectorPendingMediaRef,
}) {
  const [mediaFlushSignal] = useState(0);
  const [uploadSilent, setUploadSilent] = useState(true);
  const flushResolverRef = useRef(null);
  const queueRef = useRef([]);
  const activeRequestRef = useRef(null);
  const currentNodesRef = useRef([]);
  const hasStartedUploadRef = useRef(false);
  const dispatch = useDispatch();

  const { mediaUpload } = useSelector((state) => state.workflowSave || {});
  const { loading, error, lastResult, activeRequestId } = mediaUpload || {};

  const finishFlush = useCallback((ok, nodesOverride) => {
    const resolver = flushResolverRef.current;
    flushResolverRef.current = null;
    activeRequestRef.current = null;
    queueRef.current = [];
    hasStartedUploadRef.current = false;
    
    if (inspectorPendingMediaRef) {
      inspectorPendingMediaRef.current = false;
    }

    if (!resolver) return;
    resolver({
      ok,
      nodes: nodesOverride ?? nodesRef.current,
    });
  }, [nodesRef, inspectorPendingMediaRef]);

  const processNext = useCallback(() => {
    if (!flushResolverRef.current) return;

    if (queueRef.current.length === 0) {
      finishFlush(true, currentNodesRef.current);
      return;
    }

    const nextItem = queueRef.current[0];
    activeRequestRef.current = nextItem;
    hasStartedUploadRef.current = false;

    dispatch(uploadWorkflowMediaRequest({
      requestId: nextItem.requestId,
      nodeId: nextItem.nodeId,
      file: nextItem.file,
      silent: true
    }));
  }, [dispatch, finishFlush]);

  const flushPendingMedia = useCallback(({ silent = false } = {}) => {
    if (flushResolverRef.current) {
      return Promise.resolve({ ok: false, reason: "already_flushing" });
    }

    setUploadSilent(silent);
    const currentNodes = [...(nodesRef.current || [])];
    currentNodesRef.current = currentNodes;

    // Scan all nodes for pending uploads
    const queue = [];
    for (const node of currentNodes) {
      const pending = findPendingUploadsDeep(node.data?.config);
      for (const p of pending) {
        queue.push({
          nodeId: node.id,
          file: p.file,
          path: p.path,
          requestId: `req_${node.id}_${p.path.join("_")}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
        });
      }
    }

    if (queue.length === 0) {
      return Promise.resolve({ ok: true, nodes: currentNodes });
    }

    return new Promise((resolve) => {
      flushResolverRef.current = resolve;
      queueRef.current = queue;
      activeRequestRef.current = null;
      hasStartedUploadRef.current = false;
      processNext();
    });
  }, [nodesRef, processNext]);

  useEffect(() => {
    if (!flushResolverRef.current || !activeRequestRef.current) return;

    const activeItem = activeRequestRef.current;

    // Track when we transition into the loading state for our specific requestId
    if (activeRequestId === activeItem.requestId && loading) {
      hasStartedUploadRef.current = true;
      return;
    }

    // Wait until the active request finishes loading
    if (loading || !hasStartedUploadRef.current) return;

    if (error) {
      finishFlush(false);
      return;
    }

    if (lastResult?.requestId === activeItem.requestId) {
      const { url, filename, nodeId } = lastResult;

      const uploadResult = {
        nodeId,
        url,
        filename,
        path: activeItem.path,
      };

      const nextNodes = applyMediaUploadResultToNodes(currentNodesRef.current, uploadResult);
      currentNodesRef.current = nextNodes;

      updateNodeData(nodeId, (prevData) => {
        const matched = nextNodes.find((n) => n.id === nodeId);
        return matched?.data ?? prevData;
      });

      queueRef.current.shift();
      activeRequestRef.current = null;
      hasStartedUploadRef.current = false;
      processNext();
    }
  }, [loading, error, lastResult, activeRequestId, updateNodeData, finishFlush, processNext]);

  // Keep a dummy implementation for registered nodes compatibility
  const registerPendingUploadNode = useCallback((nodeId) => {}, []);

  return {
    mediaFlushSignal,
    uploadSilent,
    flushPendingMedia,
    registerPendingUploadNode,
  };
}
